import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Tooltip from '../components/Tooltip';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const isWeb = Platform.OS === 'web';

const IncomeTaxCalculatorScreen = ({ navigation }) => {
  const { colors } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  
  useEffect(() => {
    const onChange = (result) => {
      setScreenData(result.window);
      console.log('Screen dimensions changed:', result.window.width, 'x', result.window.height);
    };
    
    const subscription = Dimensions.addEventListener('change', onChange);
    console.log('Initial screen dimensions:', screenData.width, 'x', screenData.height);
    return () => subscription?.remove();
  }, []);
  
  const isMobile = screenData.width < 900; // Increased mobile breakpoint for better detection
  console.log('isMobile:', isMobile, 'screenWidth:', screenData.width);
  const [isCalculated, setIsCalculated] = useState(false);

  const [selectedRegime, setSelectedRegime] = useState('new'); // 'new' or 'old'
  const [showDropdowns, setShowDropdowns] = useState({
    financialYear: false,
    ageGroup: false,
    taxRegime: false,
  });
  const [formData, setFormData] = useState({
    financialYear: 'FY 2025-26 (AY 2026-27)',
    ageGroup: 'Up to 60 years',
    taxRegime: 'New Tax Regime',
    // Income fields
    basicSalary: '',
    interestIncome: '',
    selfOccupiedInterest: '',
    rentalIncome: '',
    digitalAssetIncome: '',
    otherIncome: '',
    // Deduction fields
    section80C: '',
    section80D: '',
    homeLoanInterest: '',
    npsEmployer: '',
    otherDeductions: '',
  });
  
  const [results, setResults] = useState({
    grossIncome: 0,
    totalDeductions: 0,
    taxableIncome: 0,
    oldRegimeTax: 0,
    newRegimeTax: 0,
    taxSavings: 0,
    recommendedRegime: 'New Tax Regime',
    // Add separate values for each regime
    oldRegimeTaxableIncome: 0,
    newRegimeTaxableIncome: 0,
    oldRegimeDeductions: 0,
    newRegimeDeductions: 0,
  });

  const financialYears = [
    'FY 2024-25 (AY 2025-26)',
    'FY 2025-26 (AY 2026-27)',
  ];

  const ageGroups = [
    'Up to 60 years',
    '60 to 80 years (Senior Citizen)',
    'Above 80 years (Super Senior Citizen)'
  ];

  const taxRegimes = [
    'New Tax Regime (Default)',
    'Old Tax Regime'
  ];

  useEffect(() => {
    calculateTax();
  }, [formData]);

  // Parse comma-formatted currency value
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.replace(/,/g, '')) || 0;
  };

  const calculateTax = () => {
    // Calculate total income from all sources
    const totalIncome = parseCurrency(formData.basicSalary) + 
                       parseCurrency(formData.interestIncome) + 
                       parseCurrency(formData.selfOccupiedInterest) + 
                       parseCurrency(formData.rentalIncome) + 
                       parseCurrency(formData.digitalAssetIncome) + 
                       parseCurrency(formData.otherIncome);
    
    // Calculate total deductions for old regime
    const totalDeductions = parseCurrency(formData.section80C) + 
                           parseCurrency(formData.section80D) + 
                           parseCurrency(formData.homeLoanInterest) + 
                           parseCurrency(formData.npsEmployer) + 
                           parseCurrency(formData.otherDeductions);
    
    // Standard deduction for new regime
    const standardDeduction = formData.financialYear === 'FY 2025-26 (AY 2026-27)' ? 75000 : 50000;
    
    // New regime allows standard deduction + home loan interest (but not 80C, 80D, etc.)
    const newRegimeDeductions = standardDeduction + parseCurrency(formData.homeLoanInterest);
    
    const grossIncome = totalIncome;
    const taxableIncomeOld = Math.max(0, grossIncome - totalDeductions);
    const taxableIncomeNew = Math.max(0, grossIncome - newRegimeDeductions);

    const oldRegimeTax = calculateOldRegimeTax(taxableIncomeOld);
    const newRegimeTax = calculateNewRegimeTax(taxableIncomeNew);
    const taxSavings = Math.abs(oldRegimeTax - newRegimeTax);
    const recommendedRegime = newRegimeTax <= oldRegimeTax ? 'New Tax Regime' : 'Old Tax Regime';

    setResults({
      grossIncome,
      totalDeductions: recommendedRegime === 'New Tax Regime' ? newRegimeDeductions : totalDeductions,
      taxableIncome: recommendedRegime === 'New Tax Regime' ? taxableIncomeNew : taxableIncomeOld,
      oldRegimeTax,
      newRegimeTax,
      taxSavings,
      recommendedRegime,
      // Store separate values for each regime
      oldRegimeTaxableIncome: taxableIncomeOld,
      newRegimeTaxableIncome: taxableIncomeNew,
      oldRegimeDeductions: totalDeductions,
      newRegimeDeductions: newRegimeDeductions,
    });
  };

  const calculateOldRegimeTax = (taxableIncome) => {
    let tax = 0;
    let slabs = [];
    
    // Age-based tax slabs for old regime
    if (formData.ageGroup === 'Up to 60 years') {
      slabs = [
        { min: 0, max: 250000, rate: 0 },
        { min: 250000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 }
      ];
    } else if (formData.ageGroup === '60 to 80 years (Senior Citizen)') {
      slabs = [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 500000, rate: 5 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 }
      ];
    } else { // Above 80 years (Super Senior Citizen)
      slabs = [
        { min: 0, max: 500000, rate: 0 },
        { min: 500000, max: 1000000, rate: 20 },
        { min: 1000000, max: Infinity, rate: 30 }
      ];
    }

    for (const slab of slabs) {
      if (taxableIncome > slab.min) {
        const taxableAtThisSlab = Math.min(taxableIncome, slab.max) - slab.min;
        tax += (taxableAtThisSlab * slab.rate) / 100;
      }
    }

    // Apply rebate under section 87A for old regime
    if (taxableIncome <= 500000) {
      const rebate = Math.min(tax, 12500); // Rebate up to ₹12,500
      tax = Math.max(0, tax - rebate);
    }

    // Calculate surcharge
    let surcharge = 0;
    if (taxableIncome > 5000000) {
      surcharge = tax * 0.37;
    } else if (taxableIncome > 2000000) {
      surcharge = tax * 0.25;
    } else if (taxableIncome > 1000000) {
      surcharge = tax * 0.15;
    }

    // Health and Education Cess (4%)
    const cess = (tax + surcharge) * 0.04;
    return tax + surcharge + cess;
  };

  const calculateNewRegimeTax = (taxableIncome) => {
    let tax = 0;
    let slabs = [];
    
    // Check financial year for different tax slabs
    if (formData.financialYear === 'FY 2025-26 (AY 2026-27)') {
      slabs = [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 700000, rate: 5 },
        { min: 700000, max: 1000000, rate: 10 },
        { min: 1000000, max: 1200000, rate: 15 },
        { min: 1200000, max: 1500000, rate: 20 },
        { min: 1500000, max: Infinity, rate: 30 }
      ];
    } else { // FY 2024-25
      slabs = [
        { min: 0, max: 300000, rate: 0 },
        { min: 300000, max: 700000, rate: 5 },
        { min: 700000, max: 1000000, rate: 10 },
        { min: 1000000, max: 1200000, rate: 15 },
        { min: 1200000, max: 1500000, rate: 20 },
        { min: 1500000, max: Infinity, rate: 30 }
      ];
    }

    for (const slab of slabs) {
      if (taxableIncome > slab.min) {
        const taxableAtThisSlab = Math.min(taxableIncome, slab.max) - slab.min;
        tax += (taxableAtThisSlab * slab.rate) / 100;
      }
    }

    // Apply rebate under section 87A for new regime
    if (formData.financialYear === 'FY 2025-26 (AY 2026-27)' && taxableIncome <= 1200000) {
      const rebate = Math.min(tax, 60000);
      tax = Math.max(0, tax - rebate);
    } else if (formData.financialYear === 'FY 2024-25 (AY 2025-26)' && taxableIncome <= 700000) {
      tax = 0; // Complete rebate - tax liability becomes zero for income up to ₹7L
    }

    // Calculate surcharge (reduced rates for new regime)
    let surcharge = 0;
    if (taxableIncome > 5000000) {
      surcharge = tax * 0.25; // Reduced from 37% to 25%
    } else if (taxableIncome > 2000000) {
      surcharge = tax * 0.25;
    } else if (taxableIncome > 1000000) {
      surcharge = tax * 0.15;
    }

    // Health and Education Cess (4%)
    const cess = (tax + surcharge) * 0.04;
    return tax + surcharge + cess;
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setIsCalculated(false); // Reset calculation when going back
    }
  };

  const handleCalculateTax = () => {
    calculateTax();
    setIsCalculated(true);
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderDropdown = (label, value, options, field, tooltip) => (
    <View style={styles.inputGroup}>
      <View style={styles.labelContainer}>
        <Text style={styles.label}>{label}</Text>
        {tooltip && (
          <Tooltip content={tooltip}>
            <View />
          </Tooltip>
        )}
      </View>
      <TouchableOpacity 
        style={styles.dropdownContainer}
        onPress={() => setShowDropdowns(prev => ({
          ...prev,
          [field]: !prev[field]
        }))}
      >
        <Text style={styles.dropdownText}>{value}</Text>
        <Ionicons 
          name={showDropdowns[field] ? "chevron-up" : "chevron-down"} 
          size={20} 
          color={colors.textSecondary} 
        />
      </TouchableOpacity>
      {showDropdowns[field] && (
        <View style={styles.dropdownOptions}>
          {options.map((option, index) => (
            <TouchableOpacity
              key={index}
              style={styles.dropdownOption}
              onPress={() => {
                updateFormData(field, option);
                setShowDropdowns(prev => ({ ...prev, [field]: false }));
              }}
            >
              <Text style={[styles.dropdownOptionText, value === option && styles.dropdownOptionTextSelected]}>
                {option}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    // Remove all non-digits
    const numericValue = value.replace(/[^\d]/g, '');
    // Add commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Handle currency input change
  const handleCurrencyInput = (field, value) => {
    const formattedValue = formatNumberWithCommas(value);
    updateFormData(field, formattedValue);
  };

  const renderInput = (label, value, field, placeholder, tooltip) => (
    <View style={styles.inputGroup}>
      <View style={styles.labelContainer}>
        <Text style={styles.label}>{label}</Text>
        {tooltip && (
          <Tooltip content={tooltip}>
            <View />
          </Tooltip>
        )}
      </View>
      <TextInput
        style={styles.input}
        value={value}
        onChangeText={(text) => handleCurrencyInput(field, text)}
        placeholder={placeholder}
        placeholderTextColor={colors.textSecondary}
        keyboardType="numeric"
      />
    </View>
  );

  const renderStep1 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Basic Details</Text>
      <Text style={styles.stepSubtitle}>
        Select your financial year, age group, and tax regime
      </Text>
      
      {renderDropdown(
        'Financial Year',
        formData.financialYear,
        financialYears,
        'financialYear',
        'Select the financial year for which you want to calculate tax'
      )}
      
      {renderDropdown(
        'Age Group',
        formData.ageGroup,
        ageGroups,
        'ageGroup',
        'Your age determines the basic exemption limit and tax slabs'
      )}
      
      {renderDropdown(
        'Tax Regime',
        formData.taxRegime,
        taxRegimes,
        'taxRegime',
        'Choose between old regime (with deductions) or new regime (lower rates)'
      )}
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Income Details</Text>
      <Text style={styles.stepSubtitle}>
        Enter your income from various sources
      </Text>
      
      {renderInput(
        'Income from Salary',
        formData.basicSalary,
        'basicSalary',
        '₹ 0',
        'Your total salary income including basic pay, allowances, and perquisites'
      )}
      
      {renderInput(
        'Income from Interest',
        formData.interestIncome,
        'interestIncome',
        '₹ 0',
        'Interest earned from savings accounts, fixed deposits, bonds, etc.'
      )}
      
      {renderInput(
        'Interest on Income from - Self Occupied',
        formData.selfOccupiedInterest,
        'selfOccupiedInterest',
        '₹ 0',
        'Interest income from self-occupied property'
      )}
      
      {renderInput(
        'Rental Income (excluding)',
        formData.rentalIncome,
        'rentalIncome',
        '₹ 0',
        'Income from rental properties after deducting municipal taxes'
      )}
      
      {renderInput(
        'Income from Digital Assets',
        formData.digitalAssetIncome,
        'digitalAssetIncome',
        '₹ 0',
        'Income from cryptocurrency, NFTs, and other digital assets'
      )}
      
      {renderInput(
        'Other Income',
        formData.otherIncome,
        'otherIncome',
        '₹ 0',
        'Any other income not covered in above categories'
      )}
    </View>
  );

  const renderStep3 = () => {
    if (isCalculated) {
      // Show results in the main area
      return renderResults();
    }
    
    // Show deduction form
    return (
      <View style={styles.stepContainer}>
        <Text style={styles.stepTitle}>Deductions</Text>
        <Text style={styles.stepSubtitle}>
          Add deductions (applicable for old regime)
        </Text>
        
        {renderInput(
          'Basic deductions - 80C',
          formData.section80C,
          'section80C',
          '₹ 0',
          'Investments in PPF, ELSS, NSC, life insurance premiums, etc. (Max: ₹1.5 lakh)'
        )}
        
        {renderInput(
          'Medical insurance - 80D',
          formData.section80D,
          'section80D',
          '₹ 0',
          'Health insurance premiums for self, family, and parents (Max: ₹25,000 + ₹25,000)'
        )}
        
        {renderInput(
          'Interest on Housing loan - 80EEA',
          formData.homeLoanInterest,
          'homeLoanInterest',
          '₹ 0',
          'Interest on home loan for first-time buyers (Max: ₹1.5 lakh additional)'
        )}
        
        {renderInput(
          'Employer\'s contribution to NPS - 80CCD(2)',
          formData.npsEmployer,
          'npsEmployer',
          '₹ 0',
          'Employer contribution to National Pension System (Max: 10% of salary)'
        )}
        
        {renderInput(
          'Any other deduction',
          formData.otherDeductions,
          'otherDeductions',
          '₹ 0',
          'Other eligible deductions under various sections'
        )}
      </View>
    );
  };

  const renderResults = () => (
    <View style={styles.resultsContainer}>
      {/* Header with comparison buttons */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsTitle}>Summary - {formData.financialYear}</Text>
        <View style={styles.comparisonButtons}>
          <TouchableOpacity 
            style={styles.comparisonButton}
            onPress={() => {
              setFormData({...formData, financialYear: 'FY 2025-26 (AY 2026-27)'});
              setCurrentStep(1);
              setIsCalculated(false);
            }}
          >
            <Text style={styles.comparisonButtonText}>Calculate for FY 2025-2026</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.recalculateButton}
            onPress={() => {
              setCurrentStep(1);
              setIsCalculated(false);
            }}
          >
            <Text style={styles.recalculateButtonText}>Recalculate</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recommendation Banner */}
      <View style={styles.recommendationBanner}>
        <Text style={styles.recommendationText}>
          {results.recommendedRegime} is recommended for you. It would save you ₹{formatIndianNumber(results.taxSavings)} in taxes.
        </Text>
        <TouchableOpacity style={styles.fileButton}>
          <Text style={styles.fileButtonText}>File Now</Text>
        </TouchableOpacity>
      </View>

      {/* Regime Tabs */}
      <View style={styles.regimeTabs}>
        <TouchableOpacity 
          style={[styles.regimeTab, selectedRegime === 'new' && styles.activeTab]}
          onPress={() => setSelectedRegime('new')}
        >
          <Text style={selectedRegime === 'new' ? styles.activeTabText : styles.tabText}>New regime</Text>
          {selectedRegime === 'new' && <Text style={styles.recommendedBadge}>Recommended</Text>}
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.regimeTab, selectedRegime === 'old' && styles.activeTab]}
          onPress={() => setSelectedRegime('old')}
        >
          <Text style={selectedRegime === 'old' ? styles.activeTabText : styles.tabText}>Old regime</Text>
          {selectedRegime === 'old' && <Text style={styles.recommendedBadge}>Selected</Text>}
        </TouchableOpacity>
      </View>

      {/* Main Content Area */}
      <View style={styles.mainContent}>
        {/* Left Side - Chart and Summary */}
        <View style={styles.leftContent}>
          {/* Chart Placeholder */}
          <View style={styles.chartContainer}>
            <View style={styles.chartBars}>
              <View style={[styles.chartBar, styles.totalIncomeBar]} />
              <View style={[styles.chartBar, styles.deductionBar]} />
              <View style={[styles.chartBar, styles.taxableIncomeBar]} />
              <View style={[styles.chartBar, styles.taxPayableBar]} />
            </View>
            <View style={styles.chartLegend}>
              <View style={styles.legendItem}>
                <View style={[styles.legendColor, {backgroundColor: colors.primary + '30'}]} />
                <Text style={styles.legendText}>Total income</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendColor, {backgroundColor: colors.primary + '50'}]} />
                <Text style={styles.legendText}>Taxable income</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendColor, {backgroundColor: colors.primary}]} />
                <Text style={styles.legendText}>Deduction</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendColor, {backgroundColor: colors.success || '#4CAF50'}]} />
                <Text style={styles.legendText}>Tax payable</Text>
              </View>
            </View>
          </View>

          {/* Summary Cards */}
          <View style={styles.summarySection}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Total income</Text>
              <Text style={styles.summaryValue}>₹{formatIndianNumber(results.grossIncome)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Taxable income</Text>
              <Text style={styles.summaryValue}>₹{formatIndianNumber(selectedRegime === 'new' ? results.newRegimeTaxableIncome : results.oldRegimeTaxableIncome)}</Text>
            </View>
          </View>

          <View style={styles.summarySection}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Exemption and deduction</Text>
              <Text style={styles.summaryValue}>₹{formatIndianNumber(selectedRegime === 'new' ? results.newRegimeDeductions : results.oldRegimeDeductions)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>Tax payable</Text>
              <Text style={[styles.summaryValue, styles.taxPayableValue]}>
                ₹{formatIndianNumber(selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax)}
              </Text>
            </View>
          </View>
        </View>

        {/* Right Side - Detailed Breakdown */}
        <View style={styles.rightContent}>
          <View style={styles.breakdownSection}>
            <Text style={styles.breakdownTitle}>Exemption and deduction</Text>
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Exempt Allowances</Text>
              <Text style={styles.breakdownValue}>₹0</Text>
            </View>
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Standard Deductions</Text>
              <Text style={styles.breakdownValue}>
                ₹{formatIndianNumber(selectedRegime === 'new' ? (formData.financialYear === 'FY 2025-26 (AY 2026-27)' ? 75000 : 50000) : 0)}
              </Text>
            </View>
            {selectedRegime === 'new' && parseCurrency(formData.homeLoanInterest) > 0 && (
              <View style={styles.breakdownItem}>
                <Text style={styles.breakdownLabel}>Home Loan Interest</Text>
                <Text style={styles.breakdownValue}>
                  ₹{formatIndianNumber(parseCurrency(formData.homeLoanInterest))}
                </Text>
              </View>
            )}
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Chapter VI A Deductions</Text>
              <Text style={styles.breakdownValue}>
                ₹{formatIndianNumber(selectedRegime === 'old' ? results.oldRegimeDeductions : 0)}
              </Text>
            </View>
          </View>

          <View style={styles.breakdownSection}>
            <Text style={styles.breakdownTitle}>Tax payable</Text>
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Income tax</Text>
              <Text style={styles.breakdownValue}>
                ₹{formatIndianNumber(Math.round((selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) / 1.04))}
              </Text>
            </View>
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Surcharge</Text>
              <Text style={styles.breakdownValue}>₹0</Text>
            </View>
            <View style={styles.breakdownItem}>
              <Text style={styles.breakdownLabel}>Health and education cess</Text>
              <Text style={styles.breakdownValue}>
                ₹{formatIndianNumber(Math.round((selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) - (selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) / 1.04))}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Filing Date */}
      <Text style={styles.filingDate}>ITR filing due date: July 31, 2026</Text>
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      padding: 20,
      paddingTop: isWeb ? 20 : 40,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.primaryText || '#FFFFFF',
      marginBottom: 8,
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.primaryTextSecondary || 'rgba(255, 255, 255, 0.9)',
      marginBottom: 16,
      lineHeight: 22,
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    headerTag: {
      backgroundColor: colors.primaryTextSecondary || 'rgba(255, 255, 255, 0.2)',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 12,
      fontWeight: '500',
    },
    mainContent: {
      flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
      minHeight: (isWeb && !isMobile) ? 600 : 'auto',
    },
    leftPanel: {
      flex: (isWeb && !isMobile) ? 1 : 1,
      backgroundColor: colors.background,
    },
    rightPanel: {
      width: (isWeb && !isMobile) ? 400 : '100%',
      backgroundColor: colors.surface,
      borderLeftWidth: (isWeb && !isMobile) ? 1 : 0,
      borderLeftColor: colors.border,
      padding: 20,
    },
    calculatorContainer: {
      backgroundColor: colors.card,
      margin: isMobile ? 10 : 20,
      borderRadius: 12,
      padding: 0,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    calculatorHeader: {
      padding: isMobile ? 15 : 20,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    calculatorTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    calculatorSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    progressContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 20,
      paddingHorizontal: 20,
      backgroundColor: colors.cardBackground,
    },
    progressStep: {
      alignItems: 'center',
      flex: 1,
    },
    progressStepNumber: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 8,
    },
    progressStepNumberActive: {
      backgroundColor: colors.primary,
    },
    progressStepNumberText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.textSecondary,
    },
    progressStepNumberTextActive: {
      color: colors.primaryText || '#FFFFFF',
    },
    progressStepLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    progressStepLabelActive: {
      color: colors.primary,
      fontWeight: '500',
    },
    formContent: {
      padding: 20,
    },
    stepContainer: {
      marginBottom: 20,
    },
    stepTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    stepSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 16,
    },
    labelContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    label: {
      fontSize: 14,
      fontWeight: '500',
      color: colors.text,
    },
    dropdownContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      backgroundColor: colors.background,
    },
    dropdownText: {
      fontSize: 14,
      color: colors.text,
      flex: 1,
    },
    dropdownOptions: {
      borderWidth: 1,
      borderColor: colors.border,
      borderTopWidth: 0,
      borderRadius: 8,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      backgroundColor: colors.background,
      maxHeight: 200,
    },
    dropdownOption: {
      paddingHorizontal: 12,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    dropdownOptionText: {
      fontSize: 14,
      color: colors.text,
    },
    dropdownOptionTextSelected: {
      color: colors.primary,
      fontWeight: '500',
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      fontSize: 14,
      color: colors.text,
      backgroundColor: colors.background,
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingVertical: 20,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    button: {
      backgroundColor: colors.primary,
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 8,
      flex: 1,
      marginHorizontal: 4,
    },
    buttonSecondary: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border,
    },
    buttonText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 14,
      fontWeight: '500',
      textAlign: 'center',
    },
    buttonTextSecondary: {
      color: colors.text,
    },
    resultsContainer: {
      flex: 1,
    },
    resultsTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    summaryCards: {
      marginBottom: 20,
    },
    summaryCard: {
      backgroundColor: colors.card,
      padding: 16,
      borderRadius: 8,
      marginBottom: 12,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 2,
    },
    oldRegimeCard: {
      borderLeftWidth: 4,
      borderLeftColor: colors.error || '#EF4444',
    },
    newRegimeCard: {
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    savingsCard: {
      borderLeftWidth: 4,
      borderLeftColor: colors.success || '#10B981',
    },
    regimeLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    regimeAmount: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    savingsAmount: {
      color: colors.success || '#10B981',
    },
    recommendationCard: {
      backgroundColor: colors.primary + '10',
      padding: 16,
      borderRadius: 8,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: colors.primary + '30',
    },
    recommendationTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: 8,
    },
    recommendationText: {
      fontSize: 12,
      color: colors.primary,
      lineHeight: 16,
    },
    calculateButton: {
      backgroundColor: colors.primary,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    calculateButtonText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 14,
      fontWeight: '500',
    },
    seoContent: {
      padding: 20,
      backgroundColor: colors.background,
    },
    seoSection: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    seoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    instructionStep: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 12,
      paddingLeft: 10,
    },
    stepNumber: {
      backgroundColor: colors.primary,
      color: '#FFFFFF',
      borderRadius: 12,
      width: 24,
      height: 24,
      textAlign: 'center',
      lineHeight: 24,
      fontSize: 12,
      fontWeight: 'bold',
      marginRight: 12,
    },
    stepText: {
      flex: 1,
      fontSize: 14,
      color: colors.text,
      lineHeight: 20,
    },
    faqContainer: {
      marginTop: 0,
    },
    faqItem: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      marginBottom: 12,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    taxSlabContainer: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      marginBottom: 16,
    },
    taxSlabTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.primary,
      marginBottom: 12,
    },
    taxSlabText: {
      fontSize: 14,
      color: colors.text,
      lineHeight: 20,
      marginBottom: 4,
    },
    // New Results Styles
    resultsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
      paddingBottom: 15,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    comparisonButtons: {
      flexDirection: 'row',
      gap: 10,
    },
    comparisonButton: {
      backgroundColor: colors.primary + '20',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 6,
    },
    comparisonButtonText: {
      color: colors.primary,
      fontSize: 14,
      fontWeight: '500',
    },
    recalculateButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 6,
    },
    recalculateButtonText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 14,
      fontWeight: '500',
    },
    recommendationBanner: {
      backgroundColor: colors.success + '20' || '#E8F5E8',
      padding: 16,
      borderRadius: 8,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    fileButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 6,
    },
    fileButtonText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 14,
      fontWeight: '600',
    },
    regimeTabs: {
      flexDirection: 'row',
      marginBottom: 20,
    },
    regimeTab: {
      paddingHorizontal: 20,
      paddingVertical: 12,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    activeTab: {
      borderBottomColor: colors.primary,
    },
    activeTabText: {
      color: colors.primary,
      fontWeight: '600',
      fontSize: 14,
    },
    tabText: {
      color: colors.textSecondary,
      fontSize: 14,
    },
    recommendedBadge: {
      backgroundColor: colors.success || '#4CAF50',
      color: 'white',
      fontSize: 10,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 10,
      marginTop: 4,
      textAlign: 'center',
    },
    leftContent: {
      flex: (isWeb && !isMobile) ? 1 : undefined,
    },
    rightContent: {
      flex: (isWeb && !isMobile) ? 1 : undefined,
    },
    chartContainer: {
      backgroundColor: colors.cardBackground,
      padding: 20,
      borderRadius: 8,
      marginBottom: 20,
    },
    chartBars: {
      flexDirection: 'row',
      height: 200,
      alignItems: 'flex-end',
      justifyContent: 'space-around',
      marginBottom: 20,
    },
    chartBar: {
      width: 40,
      borderRadius: 4,
    },
    totalIncomeBar: {
      height: 180,
      backgroundColor: colors.primary + '30',
    },
    deductionBar: {
      height: 60,
      backgroundColor: colors.primary,
    },
    taxableIncomeBar: {
      height: 160,
      backgroundColor: colors.primary + '50',
    },
    taxPayableBar: {
      height: 20,
      backgroundColor: colors.success || '#4CAF50',
    },
    chartLegend: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 15,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 2,
    },
    legendText: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    summarySection: {
      flexDirection: 'row',
      gap: 15,
      marginBottom: 15,
    },
    summaryCard: {
      flex: 1,
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      alignItems: 'center',
    },
    summaryLabel: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 8,
      textAlign: 'center',
    },
    summaryValue: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
    },
    taxPayableValue: {
      color: colors.success || '#4CAF50',
      fontSize: 24,
    },
    breakdownSection: {
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      marginBottom: 16,
    },
    breakdownTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 12,
    },
    breakdownItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    breakdownLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    breakdownValue: {
      fontSize: 14,
      fontWeight: '500',
      color: colors.text,
    },
    filingDate: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
      marginTop: 20,
    },
    panelHeader: {
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    panelTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 4,
    },
    panelSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 8,
    },
    panelAmount: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.success || '#4CAF50',
    },
    taxInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    infoTitle: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    infoValue: {
      fontSize: 14,
      fontWeight: '500',
      color: colors.text,
    },
    regimeInfo: {
      padding: 16,
    },
    regimeTitle: {
      fontSize: 14,
      color: colors.text,
      marginBottom: 12,
      lineHeight: 20,
    },
    fileButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 6,
      alignSelf: 'flex-start',
    },
    fileButtonText: {
      color: colors.primaryText || '#FFFFFF',
      fontSize: 14,
      fontWeight: '500',
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Income Tax Calculator for FY 2024-25 & 2025-26</Text>
        <Text style={styles.headerSubtitle}>
          Calculate your income tax liability with our advanced calculator. Compare old vs new tax regimes, plan your investments, and optimize your tax savings. Get accurate results for both regimes and find the best taxpayer.
        </Text>
        
        <View style={styles.headerTags}>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>Both Tax Regimes</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>Latest Tax Slabs</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>80C Deductions</Text>
          </View>
          <View style={styles.headerTag}>
            <Text style={styles.headerTagText}>100% Free</Text>
          </View>
        </View>
      </View>

      <View style={styles.mainContent}>
        <View style={styles.leftPanel}>
          <View style={styles.calculatorContainer}>
            <View style={styles.calculatorHeader}>
              <Text style={styles.calculatorTitle}>📊 Income Tax Calculator</Text>
              <Text style={styles.calculatorSubtitle}>
                Calculate your income tax liability for FY 2024-25 and FY 2025-26
              </Text>
            </View>

            <View style={styles.progressContainer}>
              {[1, 2, 3].map((step) => (
                <TouchableOpacity
                  key={step}
                  style={styles.progressStep}
                  onPress={() => setCurrentStep(step)}
                >
                  <View style={[
                    styles.progressStepNumber,
                    step <= currentStep && styles.progressStepNumberActive,
                  ]}>
                    <Text style={[
                      styles.progressStepNumberText,
                      step <= currentStep && styles.progressStepNumberTextActive,
                    ]}>
                      {step}
                    </Text>
                  </View>
                  <Text style={[
                    styles.progressStepLabel,
                    step <= currentStep && styles.progressStepLabelActive,
                  ]}>
                    {step === 1 && 'Basic Details'}
                    {step === 2 && 'Income Details'}
                    {step === 3 && 'Deductions'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <ScrollView style={styles.formContent} showsVerticalScrollIndicator={false}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </ScrollView>

            <View style={styles.buttonContainer}>
              {currentStep > 1 && !(currentStep === 3 && isCalculated) && (
                <TouchableOpacity
                  style={[styles.button, styles.buttonSecondary]}
                  onPress={handlePrevious}
                >
                  <Text style={[styles.buttonText, styles.buttonTextSecondary]}>Back</Text>
                </TouchableOpacity>
              )}
              
              {currentStep < 3 && (
                <TouchableOpacity style={styles.button} onPress={handleNext}>
                  <Text style={styles.buttonText}>Continue →</Text>
                </TouchableOpacity>
              )}
              
              {currentStep === 3 && !isCalculated && (
                <TouchableOpacity style={styles.button} onPress={handleCalculateTax}>
                  <Text style={styles.buttonText}>Calculate Tax</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          <View style={styles.seoContent}>
            {/* What is Income Tax Calculator Section */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>💰 What is Income Tax Calculator?</Text>
              <Text style={styles.seoText}>
                An Income Tax Calculator is a powerful financial tool that helps Indian taxpayers calculate their annual income tax liability accurately. Our advanced calculator supports both old and new tax regimes for FY 2024-25 and FY 2025-26, enabling you to compare tax obligations under different scenarios and choose the most beneficial option.
              </Text>
              <Text style={styles.seoText}>
                The calculator considers all income sources including salary, house property, capital gains, business income, and other sources. It automatically applies age-based exemptions, standard deductions, and various tax-saving investments to provide precise tax calculations with surcharge and cess.
              </Text>
            </View>

            {/* Benefits Section */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>🎯 Benefits of Using Income Tax Calculator</Text>
              
              <View style={styles.tipBox}>
                <Text style={styles.tipTitle}>💡 Pro Tip</Text>
                <Text style={styles.tipText}>Use our calculator before making tax-saving investments to optimize your tax liability and maximize savings.</Text>
              </View>

              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Accurate Tax Calculation:</Text> Get precise tax liability with latest tax slabs, surcharge, and 4% health & education cess</Text>
              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Regime Comparison:</Text> Compare old vs new tax regime to choose the most beneficial option for your income level</Text>
              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Tax Planning:</Text> Plan your investments in 80C, 80D, and other sections to minimize tax burden</Text>
              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Age-Based Calculations:</Text> Automatic application of different exemption limits for senior and super senior citizens</Text>
              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Multiple Income Sources:</Text> Calculate tax on salary, rental income, capital gains, and business income</Text>
              <Text style={styles.benefitItem}>• <Text style={styles.benefitTitle}>Instant Results:</Text> Get immediate tax calculations with detailed breakdowns and saving recommendations</Text>
            </View>

            {/* Tax Regimes Comparison */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>⚖️ Old vs New Tax Regime Comparison</Text>
              
              <View style={styles.comparisonBox}>
                <Text style={styles.comparisonTitle}>🏛️ Old Tax Regime</Text>
                <Text style={styles.comparisonText}>• Higher tax rates but extensive deductions available</Text>
                <Text style={styles.comparisonText}>• Section 80C: Up to ₹1.5 lakh deduction</Text>
                <Text style={styles.comparisonText}>• Section 80D: Health insurance premium deduction</Text>
                <Text style={styles.comparisonText}>• HRA, LTA, and other allowances exempt</Text>
                <Text style={styles.comparisonText}>• Best for taxpayers with high deduction amounts</Text>
              </View>

              <View style={styles.comparisonBox}>
                <Text style={styles.comparisonTitle}>🆕 New Tax Regime</Text>
                <Text style={styles.comparisonText}>• Lower tax rates with limited deductions</Text>
                <Text style={styles.comparisonText}>• Standard deduction of ₹75,000 for FY 2025-26</Text>
                <Text style={styles.comparisonText}>• No HRA, LTA exemptions available</Text>
                <Text style={styles.comparisonText}>• Higher basic exemption limit</Text>
                <Text style={styles.comparisonText}>• Ideal for taxpayers with minimal deductions</Text>
              </View>
            </View>

            {/* How to Use Section */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>📖 How to Use Income Tax Calculator</Text>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>1</Text>
                <Text style={styles.stepText}>Select your financial year (FY 2024-25 or FY 2025-26) and age group</Text>
              </View>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>2</Text>
                <Text style={styles.stepText}>Choose tax regime - compare old regime with deductions vs new regime with lower rates</Text>
              </View>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>3</Text>
                <Text style={styles.stepText}>Enter annual income from salary, house property, capital gains, and other sources</Text>
              </View>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>4</Text>
                <Text style={styles.stepText}>Add applicable deductions like 80C investments, health insurance, home loan interest</Text>
              </View>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>5</Text>
                <Text style={styles.stepText}>Review detailed tax calculation with breakdown of tax, surcharge, and cess</Text>
              </View>
              
              <View style={styles.instructionStep}>
                <Text style={styles.stepNumber}>6</Text>
                <Text style={styles.stepText}>Compare both regimes and choose the option that minimizes your tax liability</Text>
              </View>
            </View>

            {/* Tax Calculation Formula */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>🧮 Income Tax Calculation Formula</Text>
              
              <View style={styles.formulaBox}>
                <Text style={styles.formulaTitle}>Step 1: Calculate Gross Total Income</Text>
                <Text style={styles.formulaText}>Gross Total Income = Salary + House Property + Capital Gains + Business Income + Other Sources</Text>
              </View>

              <View style={styles.formulaBox}>
                <Text style={styles.formulaTitle}>Step 2: Calculate Taxable Income</Text>
                <Text style={styles.formulaText}>Taxable Income = Gross Total Income - Deductions (80C, 80D, etc.) - Standard Deduction</Text>
              </View>

              <View style={styles.formulaBox}>
                <Text style={styles.formulaTitle}>Step 3: Apply Tax Slabs</Text>
                <Text style={styles.formulaText}>Tax = Apply respective tax rates on taxable income as per chosen regime</Text>
              </View>

              <View style={styles.formulaBox}>
                <Text style={styles.formulaTitle}>Step 4: Add Surcharge and Cess</Text>
                <Text style={styles.formulaText}>Final Tax = Tax + Surcharge (if applicable) + 4% Health & Education Cess - Rebate u/s 87A</Text>
              </View>
            </View>

            {/* Tax Saving Strategies */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>💡 Smart Tax Saving Strategies</Text>
              
              <View style={styles.tipBox}>
                <Text style={styles.tipTitle}>🎯 Expert Advice</Text>
                <Text style={styles.tipText}>Start tax planning early in the financial year to maximize benefits and avoid last-minute investments.</Text>
              </View>

              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>Section 80C Investments:</Text> Invest in PPF, ELSS, NSC, or life insurance up to ₹1.5 lakh</Text>
              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>Health Insurance (80D):</Text> Claim up to ₹25,000 for self and ₹50,000 for parents</Text>
              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>NPS Additional Benefit:</Text> Extra ₹50,000 deduction under Section 80CCD(1B)</Text>
              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>Home Loan Interest:</Text> Deduct up to ₹2 lakh under Section 24(b)</Text>
              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>Education Loan Interest:</Text> Full interest deduction under Section 80E</Text>
              <Text style={styles.strategyItem}>• <Text style={styles.strategyTitle}>Donation Benefits:</Text> 50% or 100% deduction under Section 80G</Text>
            </View>

            {/* Important Notes */}
            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>⚠️ Important Tax Considerations</Text>
              
              <View style={styles.noteBox}>
                <Text style={styles.noteTitle}>📋 Compliance Note</Text>
                <Text style={styles.noteText}>Ensure all income sources are declared and taxes are paid by due dates to avoid penalties and interest charges.</Text>
              </View>

              <Text style={styles.noteItem}>• <Text style={styles.noteTitle}>TDS Consideration:</Text> Account for TDS deducted by employers and other sources</Text>
              <Text style={styles.noteItem}>• <Text style={styles.noteTitle}>Advance Tax:</Text> Pay advance tax if liability exceeds ₹10,000</Text>
              <Text style={styles.noteItem}>• <Text style={styles.noteTitle}>ITR Filing:</Text> File returns by July 31st for individuals (extended dates may apply)</Text>
              <Text style={styles.noteItem}>• <Text style={styles.noteTitle}>Documentation:</Text> Maintain proper records of all investments and deductions claimed</Text>
              <Text style={styles.noteItem}>• <Text style={styles.noteTitle}>Professional Help:</Text> Consult tax experts for complex income structures</Text>
            </View>

            <View style={styles.seoSection}>
              <Text style={styles.seoTitle}>❓ Frequently Asked Questions</Text>
              <View style={styles.faqContainer}>
                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What is Income Tax and how is it calculated in India?</Text>
                  <Text style={styles.faqAnswer}>
                    Income Tax is a direct tax levied by the Government of India on individuals and entities based on their annual income. It's calculated by applying tax rates on taxable income after deductions, with additional surcharge and 4% health & education cess.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 Which tax regime should I choose - Old or New?</Text>
                  <Text style={styles.faqAnswer}>
                    Choose the New regime if you have minimal deductions and want to benefit from lower tax rates. Opt for the Old regime if you have substantial deductions under 80C, 80D, HRA, LTA, etc. Our calculator helps compare both regimes.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What is the current income tax rate for FY 2025-26?</Text>
                  <Text style={styles.faqAnswer}>
                    For FY 2025-26 under New regime: 0% up to ₹4L, 5% on ₹4-8L, 10% on ₹8-12L, 15% on ₹12-16L, 20% on ₹16-20L, 25% on ₹20-24L, 30% above ₹24L. Old regime rates remain unchanged with different exemption limits.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 Is this Income Tax Calculator accurate and reliable?</Text>
                  <Text style={styles.faqAnswer}>
                    Yes, our calculator is 100% accurate and updated with the latest tax slabs, rates, and provisions as per the Income Tax Act. It includes surcharge, cess, rebates, and age-based exemptions for precise calculations.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What are the major deductions available under Section 80C?</Text>
                  <Text style={styles.faqAnswer}>
                    Section 80C allows deduction up to ₹1.5 lakh for investments in PPF, ELSS mutual funds, NSC, life insurance premiums, ULIP, tax-saving FDs, home loan principal repayment, and children's tuition fees.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How does age affect income tax calculation?</Text>
                  <Text style={styles.faqAnswer}>
                    Age determines exemption limits: Up to 60 years - ₹2.5L exemption, 60-80 years (Senior Citizens) - ₹3L exemption, Above 80 years (Super Senior Citizens) - ₹5L exemption. Higher exemptions mean lower tax liability.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What is Standard Deduction and how much can I claim?</Text>
                  <Text style={styles.faqAnswer}>
                    Standard Deduction is a flat deduction available to salaried individuals. For FY 2024-25, it's ₹50,000, and for FY 2025-26, it's increased to ₹75,000. It's available in both old and new tax regimes.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How is surcharge calculated on income tax?</Text>
                  <Text style={styles.faqAnswer}>
                    Surcharge is levied on high-income earners: 10% on income ₹50L-₹1Cr, 15% on ₹1-2Cr, 25% on ₹2-5Cr, 37% above ₹5Cr. Marginal relief ensures effective rate doesn't exceed certain limits.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What is Section 87A rebate and who can claim it?</Text>
                  <Text style={styles.faqAnswer}>
                    Section 87A provides rebate up to ₹12,500 for taxpayers with income up to ₹5 lakh in new regime and ₹7 lakh in old regime for FY 2025-26. This can reduce tax liability to zero for eligible taxpayers.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 Can I switch between old and new tax regimes every year?</Text>
                  <Text style={styles.faqAnswer}>
                    Salaried individuals can switch between regimes annually by informing their employer. Business/professional income earners can switch but once opted for new regime, they cannot revert to old regime.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How to calculate tax on capital gains?</Text>
                  <Text style={styles.faqAnswer}>
                    Short-term capital gains are taxed as per applicable slab rates. Long-term capital gains on equity (>₹1L) are taxed at 12.5%, and on other assets at 12.5% with indexation benefit removed from FY 2024-25.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What happens if I don't file ITR on time?</Text>
                  <Text style={styles.faqAnswer}>
                    Late filing attracts penalty under Section 234F: ₹5,000 for income above ₹5L, ₹1,000 for income below ₹5L. Additionally, you may face interest charges and lose certain benefits like carry forward of losses.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How much can I save in health insurance under Section 80D?</Text>
                  <Text style={styles.faqAnswer}>
                    Section 80D allows deduction up to ₹25,000 for self/family health insurance, additional ₹25,000 for parents (₹50,000 if parents are senior citizens), and ₹5,000 for preventive health check-ups.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 Is HRA exemption available in the new tax regime?</Text>
                  <Text style={styles.faqAnswer}>
                    No, HRA exemption is not available in the new tax regime. Only standard deduction and a few specified deductions are allowed. If you receive significant HRA, the old regime might be more beneficial.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How to calculate advance tax and when to pay?</Text>
                  <Text style={styles.faqAnswer}>
                    Advance tax is payable if tax liability exceeds ₹10,000. Pay 15% by June 15, 45% by Sept 15, 75% by Dec 15, and 100% by March 15. Use our calculator to estimate liability and plan payments.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 Can NRIs use this income tax calculator?</Text>
                  <Text style={styles.faqAnswer}>
                    Yes, NRIs can use this calculator for Indian income. However, NRIs have different tax implications, exemptions, and TDS rates. Consult a tax expert for comprehensive NRI tax planning and compliance.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 What documents are required for claiming deductions?</Text>
                  <Text style={styles.faqAnswer}>
                    Maintain investment proofs (80C), health insurance receipts (80D), home loan certificates, donation receipts (80G), NPS statements (80CCD), and salary slips. Digital copies are acceptable for most deductions.
                  </Text>
                </View>

                <View style={styles.faqItem}>
                  <Text style={styles.faqQuestion}>🔹 How does this calculator help in tax planning?</Text>
                  <Text style={styles.faqAnswer}>
                    Our calculator helps you compare tax liability under different scenarios, plan optimal investments, choose the right tax regime, estimate advance tax requirements, and maximize tax savings through strategic financial planning.
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.faqContainer}>
              <Text style={styles.seoTitle}>📊 Income Tax Slabs FY 2024-25 & FY 2025-26</Text>
              
              <Text style={styles.seoText}>
                <Text style={styles.faqQuestion}>New Tax Regime (FY 2024-25):</Text>
                {'\n'}• Up to ₹3,00,000: 0%
                {'\n'}• ₹3,00,001 - ₹7,00,000: 5%
                {'\n'}• ₹7,00,001 - ₹10,00,000: 10%
                {'\n'}• ₹10,00,001 - ₹12,00,000: 15%
                {'\n'}• ₹12,00,001 - ₹15,00,000: 20%
                {'\n'}• Above ₹15,00,000: 30%
              </Text>

              <Text style={styles.seoText}>
                <Text style={styles.faqQuestion}>New Tax Regime (FY 2025-26):</Text>
                {'\n'}• Up to ₹4,00,000: 0%
                {'\n'}• ₹4,00,001 - ₹8,00,000: 5%
                {'\n'}• ₹8,00,001 - ₹12,00,000: 10%
                {'\n'}• ₹12,00,001 - ₹16,00,000: 15%
                {'\n'}• ₹16,00,001 - ₹20,00,000: 20%
                {'\n'}• ₹20,00,001 - ₹24,00,000: 25%
                {'\n'}• Above ₹24,00,000: 30%
              </Text>

              <Text style={styles.seoText}>
                <Text style={styles.faqQuestion}>Old Tax Regime (All Years):</Text>
                {'\n'}• Up to 60 years: ₹2,50,000 exemption
                {'\n'}• 60-80 years: ₹3,00,000 exemption
                {'\n'}• Above 80 years: ₹5,00,000 exemption
                {'\n'}• 5% on next ₹2,50,000
                {'\n'}• 20% on ₹5,00,000 - ₹10,00,000
                {'\n'}• 30% above ₹10,00,000
              </Text>
            </View>

            <View style={styles.faqContainer}>
              <Text style={styles.seoTitle}>✨ Key Features</Text>
              
              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Comprehensive Tax Calculation</Text>
                <Text style={styles.faqAnswer}>
                  Calculate tax with surcharge, cess, and rebates under Section 87A for accurate results.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Age-Based Tax Slabs</Text>
                <Text style={styles.faqAnswer}>
                  Different exemption limits for regular taxpayers, senior citizens, and super senior citizens.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Regime Comparison</Text>
                <Text style={styles.faqAnswer}>
                  Side-by-side comparison of old vs new tax regime to help you choose the best option.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Latest Tax Updates</Text>
                <Text style={styles.faqAnswer}>
                  Updated with latest tax slabs for FY 2024-25 and FY 2025-26 as per Union Budget.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Deduction Planning</Text>
                <Text style={styles.faqAnswer}>
                  Plan your investments in 80C, 80D, and other sections to optimize tax savings.
                </Text>
              </View>
            </View>

            <View style={styles.faqContainer}>
              <Text style={styles.seoTitle}>💡 Tax Saving Tips</Text>
              
              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Maximize Section 80C Deductions</Text>
                <Text style={styles.faqAnswer}>
                  Invest up to ₹1.5 lakh in PPF, ELSS, NSC, or life insurance to save tax.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Health Insurance Benefits</Text>
                <Text style={styles.faqAnswer}>
                  Claim up to ₹25,000 for health insurance premiums under Section 80D.
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 Home Loan Interest</Text>
                <Text style={styles.faqAnswer}>
                  Deduct up to ₹2 lakh for home loan interest under Section 24(b).
                </Text>
              </View>

              <View style={styles.faqItem}>
                <Text style={styles.faqQuestion}>🔹 NPS Additional Benefit</Text>
                <Text style={styles.faqAnswer}>
                  Extra ₹50,000 deduction for NPS contributions under Section 80CCD(1B).
                </Text>
              </View>
            </View>
          </View>
        </View>

        {currentStep >= 1 && !isMobile && (
          <View style={styles.rightPanel}>
            <View style={styles.panelHeader}>
              <Text style={styles.panelTitle}>Tax Profile</Text>
              <Text style={styles.panelSubtitle}>Tax payable</Text>
              <Text style={styles.panelAmount}>
                ₹{formatIndianNumber(selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax)}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Income tax</Text>
              <Text style={styles.infoValue}>
                ₹{formatIndianNumber(Math.round((selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) / 1.04))}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Surcharge</Text>
              <Text style={styles.infoValue}>₹0</Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Health and education cess</Text>
              <Text style={styles.infoValue}>
                ₹{formatIndianNumber(Math.round((selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) - (selectedRegime === 'new' ? results.newRegimeTax : results.oldRegimeTax) / 1.04))}
              </Text>
            </View>
            
            <View style={styles.regimeInfo}>
              <Text style={styles.regimeTitle}>
                {selectedRegime === 'new' ? 'New Tax Regime' : 'Old Tax Regime'} is selected. 
                {results.newRegimeTax < results.oldRegimeTax 
                  ? ` New regime would save you ₹${formatIndianNumber(results.oldRegimeTax - results.newRegimeTax)} in taxes.`
                  : ` Old regime would save you ₹${formatIndianNumber(results.newRegimeTax - results.oldRegimeTax)} in taxes.`
                }
              </Text>
              <TouchableOpacity style={styles.fileButton}>
                <Text style={styles.fileButtonText}>File Now</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default IncomeTaxCalculatorScreen;